'use client';

import { c as _c } from "react/compiler-runtime";
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { useMediaState } from '@7f-ui/hooks/use-media-state';
import { trackCtaSelection } from '@feat/feat-tracking/cta';
import { Separator } from '@shared/composites';
import { useState } from 'react';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { getSelectionFromFilter, validateSelections } from '../_lib/experience-booking-utils';
import type { ExperienceFilter, SelectedOptions } from '../_lib/experience-state-types';
import { AppExperienceContactFormLink } from './app-experience-contact-form-link';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppPersonSelect } from './app-person-select';
type AppPersonModalContentProps = {
  onSubmit?: (filter: ExperienceFilter) => Promise<void> | void;
  showContactFormLink?: boolean;
  isSubmitting?: boolean;
};
type PaxSelectionData = {
  selectedOptions: SelectedOptions | null;
  selectedVariant?: string | null;
};
export function AppPersonModalContent({
  onSubmit,
  showContactFormLink = true,
  isSubmitting = false
}: AppPersonModalContentProps) {
  const isMobile = useMediaState('mobile');
  const {
    filter,
    experience,
    variants,
    setFilter
  } = useExperienceFilterContext();
  const {
    getVariantOptions
  } = useExperienceOptions(variants);
  const [selectedOptions, setSelectedOptions] = useState(filter.selectedOptions);
  const [isUpdatingFilter, setIsUpdatingFilter] = useState(false);
  const selectedVariant = filter.selectedVariant ?? variants[0].id;
  const isValidSelection = validateSelections(variants, selectedOptions, selectedVariant);
  const isBusy = isUpdatingFilter || isSubmitting;
  const onChange = ({
    selectedOptions: nextSelectedOptions
  }: PaxSelectionData) => {
    setSelectedOptions(nextSelectedOptions);
  };
  const onConfirm = async () => {
    const updatedFilter = {
      ...filter,
      selectedOptions
    };
    setIsUpdatingFilter(true);
    try {
      await setFilter(updatedFilter);
      await onSubmit?.(updatedFilter);
      const selection = getSelectionFromFilter(updatedFilter, variants);
      trackCtaSelection('take over - persons', {
        selection,
        type: 'experience'
      });
    } finally {
      setIsUpdatingFilter(false);
    }
  };
  return <Modal.Content className="py-0" renderFooter={() => <Modal.Footer>
          {showContactFormLink && !isMobile && <Modal.Footer.InfoBox>
              <AppExperienceContactFormLink experiencePublicId={experience.publicId} />
            </Modal.Footer.InfoBox>}
          <Modal.Footer.Actions>
            <Modal.Footer.Button disabled={isBusy || !isValidSelection} isLoading={isBusy} onClick={() => void onConfirm()}>
              Übernehmen
            </Modal.Footer.Button>
          </Modal.Footer.Actions>
        </Modal.Footer>}>
      <div className="py-6">
        <AppPersonSelect onChange={onChange} options={getVariantOptions(selectedVariant)} priceCategory={experience.priceCategory} selectedOptions={selectedOptions} variantId={selectedVariant} />
      </div>
      {variants.length > 1 && <MessageBox type="info">
          Die Auswahlmöglichkeiten und Preise hängen von der gewählten Option ab.
        </MessageBox>}

      {showContactFormLink && isMobile && <Separator fullWidth />}

      {showContactFormLink && isMobile && <div className="mt-6">
          <AppExperienceContactFormLink experiencePublicId={experience.publicId} />
        </div>}
    </Modal.Content>;
}
type AppPersonModalProps = {
  isOpen: boolean;
  title?: string;
  showContactFormLink?: boolean;
  onClose: VoidFunction;
  onSubmit?: (filter: ExperienceFilter) => Promise<void> | void;
  isSubmitting?: boolean;
};
export function AppPersonModal(t0) {
  const $ = _c(10);
  const {
    isOpen,
    title,
    showContactFormLink,
    onClose,
    onSubmit,
    isSubmitting
  } = t0;
  let t1;
  if ($[0] !== isSubmitting || $[1] !== onClose || $[2] !== onSubmit || $[3] !== showContactFormLink || $[4] !== title) {
    t1 = props => <><Modal.Header onClose={onClose}>{title ?? "Personen w\xE4hlen"}</Modal.Header><AppPersonModalContent {...props} isSubmitting={isSubmitting} showContactFormLink={showContactFormLink} onSubmit={onSubmit} /></>;
    $[0] = isSubmitting;
    $[1] = onClose;
    $[2] = onSubmit;
    $[3] = showContactFormLink;
    $[4] = title;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isOpen || $[7] !== onClose || $[8] !== t1) {
    t2 = <Modal isOpen={isOpen} onClose={onClose}>{t1}</Modal>;
    $[6] = isOpen;
    $[7] = onClose;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}